import { useState } from "react";
import { useCleanerRecord } from "@/hooks/useCleaner";
import { useAllCleanerPurchases, CleanerPurchaseWithItem } from "@/hooks/useCleanerPurchases";
import { CleanerBottomNav } from "@/components/layout/CleanerBottomNav";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Receipt, ShoppingCart, Package, Calendar, DollarSign, Hash, FileText, Image } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "@/hooks/useTranslation";

const CleanerPurchases = () => {
  const { tDynamic } = useTranslation();
  const { data: cleaner, isLoading: cleanerLoading } = useCleanerRecord();
  const { data: purchases = [], isLoading: purchasesLoading } = useAllCleanerPurchases(cleaner?.id);
  const [selectedPurchase, setSelectedPurchase] = useState<CleanerPurchaseWithItem | null>(null);

  const isLoading = cleanerLoading || purchasesLoading;

  const statusConfig: Record<string, { label: string; className: string }> = {
    pending_approval: { label: tDynamic("Pending"), className: "bg-amber-100 text-amber-700" },
    auto_approved: { label: tDynamic("Paid automatically"), className: "bg-green-100 text-green-700" },
    approved: { label: tDynamic("Approved"), className: "bg-blue-100 text-blue-700" },
    paid: { label: tDynamic("Paid"), className: "bg-green-100 text-green-700" },
    rejected: { label: tDynamic("Rejected"), className: "bg-red-100 text-red-700" },
  };

  const getItemName = (purchase: CleanerPurchaseWithItem) => {
    if (purchase.type === "request_order") {
      return purchase.inventory_items?.item_name || tDynamic("Order Request");
    }
    return purchase.inventory_items?.item_name || tDynamic("Purchase");
  };

  const getTypeLabel = (type: string) => {
    return type === "request_order" ? tDynamic("Request") : tDynamic("Purchase");
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="p-4 border-b">
        <h1 className="text-xl font-bold">{tDynamic("My Purchases")}</h1>
        <p className="text-sm text-muted-foreground">{tDynamic("Track your requests and reimbursements")}</p>
      </div>

      <div className="p-4 space-y-3">
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">{tDynamic("Loading...")}</div>
        ) : purchases.length === 0 ? (
          <div className="text-center py-12">
            <Receipt className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">{tDynamic("No purchases or requests yet")}</p>
          </div>
        ) : (
          purchases.map((purchase) => {
            const status = statusConfig[purchase.status] || statusConfig.pending_approval;
            return (
              <Card
                key={purchase.id}
                className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setSelectedPurchase(purchase)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="p-2 rounded-full bg-muted">
                      {purchase.type === "request_order" ? (
                        <ShoppingCart className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <Receipt className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{getItemName(purchase)}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {getTypeLabel(purchase.type)}
                        </Badge>
                        {purchase.amount > 0 && (
                          <span className="text-sm font-medium text-muted-foreground">
                            ${purchase.amount.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className={`${status.className} hover:${status.className}`}>
                      {status.label}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(purchase.created_at), "MMM d")}
                    </span>
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>

      {/* Detail Sheet */}
      <Sheet open={!!selectedPurchase} onOpenChange={(open) => !open && setSelectedPurchase(null)}>
        <SheetContent side="bottom" className="h-auto max-h-[85vh] rounded-t-xl overflow-y-auto">
          {selectedPurchase && (
            <>
              <SheetHeader className="text-left pb-4">
                <SheetTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  {getItemName(selectedPurchase)}
                </SheetTitle>
              </SheetHeader>

              <div className="space-y-4 pb-6">
                {/* Status */}
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm text-muted-foreground">{tDynamic("Status")}</span>
                  <Badge className={`${statusConfig[selectedPurchase.status]?.className} hover:${statusConfig[selectedPurchase.status]?.className}`}>
                    {statusConfig[selectedPurchase.status]?.label}
                  </Badge>
                </div>

                {/* Type */}
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  {selectedPurchase.type === "request_order" ? (
                    <ShoppingCart className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <Receipt className="w-4 h-4 text-muted-foreground" />
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">{tDynamic("Type")}</p>
                    <p className="font-medium">{getTypeLabel(selectedPurchase.type)}</p>
                  </div>
                </div>

                {/* Quantity */}
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <Hash className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">{tDynamic("Quantity")}</p>
                    <p className="font-medium">{selectedPurchase.quantity}</p>
                  </div>
                </div>

                {/* Amount (if purchase) */}
                {selectedPurchase.amount > 0 && (
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">{tDynamic("Amount")}</p>
                      <p className="font-medium">${selectedPurchase.amount.toFixed(2)}</p>
                    </div>
                  </div>
                )}

                {/* Date */}
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">{tDynamic("Created")}</p>
                    <p className="font-medium">{format(new Date(selectedPurchase.created_at), "MMM d, yyyy 'at' h:mm a")}</p>
                  </div>
                </div>

                {/* Note */}
                {selectedPurchase.note && (
                  <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                    <FileText className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">{tDynamic("Note")}</p>
                      <p className="font-medium">{selectedPurchase.note}</p>
                    </div>
                  </div>
                )}

                {/* Receipt Image */}
                {selectedPurchase.receipt_url && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Image className="w-4 h-4" />
                      <span>{tDynamic("Receipt")}</span>
                    </div>
                    <img
                      src={selectedPurchase.receipt_url}
                      alt={tDynamic("Receipt")}
                      className="w-full rounded-lg border"
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <CleanerBottomNav />
    </div>
  );
};

export default CleanerPurchases;
